import { useState, useRef } from "react";
import { Upload, Loader2, FileUp } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../../services/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const FIELDS = ["first_name", "last_name", "company", "title", "email", "phone_office", "phone_mobile", "street", "apt", "city", "state", "zip_code", "notes"];

const ImportContactsDialog = ({ open, onOpenChange, onImported }) => {
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const reset = () => { setRows(null); setSelected(new Set()); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("parse");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = (await apiClient.post("/shared-contacts/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      setRows(r.rows);
      setSelected(new Set(r.rows.map((_, i) => i).filter((i) => !r.rows[i].duplicate)));
      if (r.duplicates) toast.info(`${r.duplicates} already in Contacts — unticked`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read that file");
    } finally { setBusy(""); e.target.value = ""; }
  };

  const toggle = (i) => setSelected((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const allOn = rows && selected.size === rows.length;

  const doImport = async () => {
    const contacts = rows.filter((_, i) => selected.has(i)).map((r) => Object.fromEntries(FIELDS.map((k) => [k, r[k] || ""])));
    if (contacts.length === 0) return;
    setBusy("import");
    try {
      const r = (await apiClient.post("/shared-contacts/import", { contacts })).data;
      toast.success(`Imported ${r.created} contact${r.created === 1 ? "" : "s"}${r.skipped_duplicates ? `, skipped ${r.skipped_duplicates} duplicate(s)` : ""} — syncing to Outlook in one batch`);
      if (r.errors?.length) toast.warning(`${r.errors.length} row(s) could not be imported`, { description: r.errors.slice(0, 3).join("; ") });
      onImported?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="import-contacts-dialog">
        <DialogHeader><DialogTitle>Import Contacts</DialogTitle></DialogHeader>
        {!rows ? (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border-2 border-dashed p-8 text-center space-y-3">
              <FileUp className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="text-sm">Upload a <strong>vCard (.vcf)</strong> or <strong>CSV</strong> export.</p>
              <input ref={fileRef} type="file" accept=".vcf,.vcard,.csv,text/vcard,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="import-choose-file-btn">
                {busy === "parse" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Choose file
              </Button>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong>iCloud:</strong> icloud.com → Contacts → click any contact, press ⌘A / Ctrl+A → gear icon (bottom-left) → Export vCard.</p>
              <p><strong>iPhone:</strong> Contacts app → Lists → touch and hold a list → Export.  <strong>Outlook / Google:</strong> export as vCard or CSV.</p>
              <p>Contacts whose email already exists are skipped. Sync to Outlook happens once, after the whole batch.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={allOn} onCheckedChange={(v) => setSelected(v ? new Set(rows.map((_, i) => i)) : new Set())} data-testid="import-select-all" /> Select all
              </label>
              <span className="text-muted-foreground" data-testid="import-selected-count">{selected.size} of {rows.length} selected</span>
            </div>
            <div className="max-h-[50vh] overflow-auto rounded border divide-y" data-testid="import-preview">
              {rows.map((r, i) => (
                <label key={`${r.email || r.display_name}-${i}`} className={`grid grid-cols-[auto_1.3fr_1fr_1.4fr_1fr] gap-3 px-3 py-2 text-sm items-center cursor-pointer ${r.duplicate ? "opacity-60" : ""}`} data-testid={`import-row-${i}`}>
                  <Checkbox checked={selected.has(i)} onCheckedChange={() => toggle(i)} data-testid={`import-row-${i}-check`} />
                  <div>
                    <div className="font-medium">{r.display_name}</div>
                    <div className="text-xs text-muted-foreground">{[r.title, r.company].filter(Boolean).join(" · ")}</div>
                  </div>
                  <div className="text-xs">
                    {r.phone_office && <div>{r.phone_office} <span className="text-muted-foreground">office</span></div>}
                    {r.phone_mobile && <div>{r.phone_mobile} <span className="text-muted-foreground">mobile</span></div>}
                  </div>
                  <div className="text-xs break-all">
                    <div>{r.email || <span className="text-muted-foreground">no email — app only</span>}</div>
                    <div className="text-muted-foreground">{[r.street, r.city, r.state, r.zip_code].filter(Boolean).join(", ")}</div>
                  </div>
                  <div className="text-right">{r.duplicate && <Badge variant="outline" className="text-xs bg-gray-100">Already in Contacts</Badge>}</div>
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={reset} disabled={!!busy}>Choose another file</Button>
              <Button onClick={doImport} disabled={!!busy || selected.size === 0} data-testid="import-confirm-btn">
                {busy === "import" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Import {selected.size} contact{selected.size === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportContactsDialog;
